import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import FormatCurrency from 'components/Common/FormatCurrency'

const ClickableIcon = styled(Clickable)`
  // Vertically align the icon with text
  display: flex;
`

interface Props {
  /**
   * @option estimate - when we don't have enough data to calculate the exact points e.g. no dates entered
   * @option exact - when we have enough data to calculate the exact points the customer will earn
   * @option additional - 'earn an additional' amount of points
   * @option nominal - we want to display that the user will earn points without showing the numerical amount
   * @option ratio - we want to display the ratio of points earned per currency unit: e.g. 1.5 points per $1
   */
  calculationType: 'estimate' | 'exact' | 'additional' | 'nominal' | 'ratio'
  earnablePoints: number
  statusCredits?: number
  pointsEarnRatio?: number
  trailingCaption?: string
  /** does not propagate and is default-prevented */
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

function PriceRowLoyaltyPointsContent({
  calculationType,
  earnablePoints,
  statusCredits,
  pointsEarnRatio,
  trailingCaption,
  onInfoIconClick,
}: Props) {
  const intl = useIntl()

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onInfoIconClick?.(event)
  }, [onInfoIconClick])

  const showPoints = calculationType !== 'nominal'
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const hasStatusCredits = showPoints && !!statusCredits

  const pointsValue = <FormatLoyaltyPoints abbreviated points={earnablePoints} />
  const statusCreditsValue = <FormatLoyaltyStatusCredits statusCredits={statusCredits ?? 0} />

  const earningLabel = (() => {
    if (!showPoints) {
      return intl.formatMessage(
        {
          id: 'Z0d28cAwUmnpyMFGfA1V',
          defaultMessage: 'Earn {programName} Points',
          description: 'Loyalty points text when showing nominal earn message without a numeric value',
        },
        { programName: loyaltyProgramName },
      )
    }

    switch (calculationType) {
      case 'estimate':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'FM9g9qtbOKRnsvCc2qts',
                defaultMessage: 'Earn from {points} + {statusCredits}',
                description: 'Loyalty points text when showing estimated minimum earnable points and status credits',
              },
              { points: pointsValue, statusCredits: statusCreditsValue },
            )
          : intl.formatMessage(
              {
                id: 'fs6IlgESLAyldDQfoSSx',
                defaultMessage: 'Earn from {points}',
                description: 'Loyalty points text when showing estimated minimum earnable points',
              },
              { points: pointsValue },
            )
      case 'exact':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'bpmFF1ymVaoDl68Ta99X',
                defaultMessage: 'Earn {points} + {statusCredits}',
                description: 'Loyalty points text when showing exact earnable points and status credits',
              },
              { points: pointsValue, statusCredits: statusCreditsValue },
            )
          : intl.formatMessage(
              {
                id: 'jOnpMSWUr0T-82M5ubX1',
                defaultMessage: 'Earn {points}',
                description: 'Loyalty points text when showing exact earnable points',
              },
              { points: pointsValue },
            )
      case 'additional':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: '_bLnoT4vqfbXMmGThNir',
                defaultMessage: 'Earn an additional {points} + {statusCredits}',
                description: 'Loyalty points text when showing additional earnable points and status credits',
              },
              { points: pointsValue, statusCredits: statusCreditsValue },
            )
          : intl.formatMessage(
              {
                id: 'zemhJqrSdvyoJADjcz74',
                defaultMessage: 'Earn an additional {points}',
                description: 'Loyalty points text when showing additional earnable points',
              },
              { points: pointsValue },
            )
      case 'ratio':
        return hasStatusCredits
          ? intl.formatMessage(
              {
                id: 'kyuPeyUAoGM1JkZwN2XK',
                defaultMessage: 'Earn {points} per {price} + {statusCredits}',
                description: 'Loyalty points text showing points earned per currency unit and status credits',
              },
              { points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />, price: <FormatCurrency value={1} />, statusCredits: statusCreditsValue },
            )
          : intl.formatMessage(
              {
                id: 'odQ5lnDGtPrOiBRUoLQH',
                defaultMessage: 'Earn {points} per {price}',
                description: 'Loyalty points text showing points earned per one unit of currency',
              },
              { points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />, price: <FormatCurrency value={1} /> },
            )
    }
  })()

  return <Group direction="horizontal" wrap="wrap" gap={4}>
    <span>{earningLabel}</span>
    <Group direction="horizontal" verticalAlign="center" gap={4} as="span" noDisplayWhenEmpty>
      {!!trailingCaption && <Caption variant="medium" colour="neutral-three" weight="normal" as="span">
        {trailingCaption}
      </Caption>}
      {onInfoIconClick && <ClickableIcon onClick={handleClick}>
        <InlineIcon Icon={LineInfoCircleIcon} colour="neutral-one" />
      </ClickableIcon>}
    </Group>
  </Group>
}

export default PriceRowLoyaltyPointsContent
